/**
 * TerminalPage — the calm paper pages for §5 states, the decline/withdraw terminals and the public
 * opt-out surface: one sentence and an honest account of what happens next, centred on paper with
 * just the mini thread mark (per the mock's edge states). Self-contained (no session context
 * required) so it doubles as the fallback when a token fails to resolve, and as the whole layout for
 * the zero-session opt-out page.
 *
 * The mock frames these as "one sentence, one action" — but for the §5 states none of the actions it
 * draws has an endpoint behind it, so what ships is the sentence. See the `expired` case for the
 * full reasoning; the short version is that a control which cannot do what it says is a worse dead
 * end than an honest one. The opt-out kinds are the exception that proves the rule: there IS a real
 * endpoint there, so that page gets its button, through `children`.
 *
 * Copy uses the typographic apostrophe (’) to match every other screen in the flow. These strings
 * are TS, not JSX, so `&rsquo;` would render literally — the character itself is the way.
 */
import type { ReactNode } from 'react';
import { ThreadMark } from './ThreadMark';

export type TerminalKind =
  | 'declined'
  | 'expired'
  | 'withdrawn'
  | 'under-review'
  | 'not-found'
  // The public opt-out surface (WP-B6, routes/OptOut.tsx). Not §5 states and not reached from a
  // session — they hang off the invite footer's "Never contact me again" link — but they are the
  // same calm paper page doing the same job, so they share it rather than duplicating the layout.
  | 'opt-out-confirm'
  | 'opt-out-done'
  | 'opt-out-unreadable';

/**
 * Title, one sentence, and an optional quiet closing line.
 *
 * There is no `actionLabel`/`onAction` pair, and there will not be one: a slot standing ready is
 * precisely how a button wired to `() => {}` gets shipped at a dead end (see the `expired` note
 * below — none of the §5 states has a verb behind it in the contract). The one state that DOES have
 * a real endpoint, `opt-out-confirm`, puts its button through `children`, where the call that fills
 * it has to be passed in by the caller and cannot be left empty.
 */
interface Copy {
  title: string;
  body: string;
  hint?: string;
}

function copyFor(kind: TerminalKind, name?: string): Copy {
  const who = name ?? 'the candidate';
  switch (kind) {
    case 'declined':
      return {
        title: 'No problem at all.',
        body: `Thanks for letting us know — we’ve told ${who}.`,
        hint: 'You can close this page.',
      };
    case 'expired':
      // The mock offers "Send me a new link", which pings the candidate to resend. THAT BUTTON IS
      // NOT BUILT, and its absence is deliberate: the only resend endpoint in the contract is
      // candidate-authenticated (`POST /api/references/{id}/resend`) and there is no referee-facing
      // verb to trigger it. A button wired to `() => {}` at a dead end is worse than no button —
      // it promises a rescue that never comes. Worse, the obvious implementation is a
      // notification send reachable from an EXPIRED token, i.e. a spam vector aimed at the
      // candidate, which needs a rate-limit and abuse design rather than an afternoon. So the page
      // stays honest: it names the one action that does work, which the referee performs themselves.
      // Flagged to the founder alongside the "email me a copy" gap.
      return {
        title: 'This link has expired.',
        body: `Happens to the best of links. Ask ${who} to send you a fresh one and you can pick up where you left off.`,
        hint: 'Nothing you did is lost.',
      };
    case 'withdrawn':
      // No pronoun for the candidate anywhere on this page: the mock's "She's withdrawn the
      // request" works for the demo cast but the product knows a display name and nothing else,
      // and guessing someone's pronouns from their name is exactly the kind of small
      // disrespect this surface cannot afford. The passive voice costs nothing here.
      return {
        title: name ? `${name} no longer needs this one.` : 'This request has been withdrawn.',
        body: 'The request has been withdrawn — nothing more is needed from you.',
        hint: 'You can close this page.',
      };
    case 'under-review':
      return {
        title: 'Nearly there.',
        body: "We’re just checking a couple of things — we’ll email you as soon as your draft is ready.",
        hint: 'Nothing is needed from you right now.',
      };
    case 'not-found':
      return {
        title: 'We couldn’t find this link.',
        body: `Double-check the link in your message, or ask ${who} to send it again.`,
      };

    // --- the public opt-out surface (WP-B6) -------------------------------------------------
    // No candidate name anywhere in these three. The opt-out token carries an address and nothing
    // else — there is no reference request behind it, and by the time someone is asking never to be
    // contacted again, naming the person who put them on this list is the wrong note entirely.
    case 'opt-out-confirm':
      return {
        title: 'Never hear from us again?',
        // The scope is stated up front because it is bigger than this one request, and the
        // irreversibility is stated because it is: nothing on this surface can undo it.
        body: 'Confirm below and we’ll block every future invite to this address — from anyone using By My Word. It takes effect immediately, and there’s no undo.',
        hint: 'Tapped this by mistake? Nothing has happened yet — just close this page.',
      };
    case 'opt-out-done':
      // Wording that is equally true for a first opt-out and a repeat click, because the endpoint
      // answers both with the same 200 on purpose (it must not be an oracle for who is already
      // suppressed). "We've blocked …" is a statement about the list, not about what just happened,
      // so this page never claims to know which of the two it was.
      return {
        title: 'Done — that’s the last of it.',
        body: 'We’ve blocked every future invite to this address, from anyone using By My Word.',
        hint: 'You can close this page.',
      };
    case 'opt-out-unreadable':
      // NOT "expired": suppression tokens deliberately never expire ("a 'stop contacting me' link
      // must not go stale"), so nothing here may suggest waiting or coming back later. The
      // overwhelmingly likely cause is a link an email client wrapped across two lines. The
      // guaranteed way out is in `children` — a broken opt-out link with no fallback is the
      // compliance failure this page exists to prevent.
      return {
        title: 'We couldn’t read this link.',
        body: 'Email apps sometimes break a long link across two lines. Try opening it again from the message — or send us a note and we’ll take care of it by hand.',
      };
  }
}

interface TerminalPageProps {
  kind: TerminalKind;
  candidateName?: string;
  children?: ReactNode;
}

export function TerminalPage({ kind, candidateName, children }: TerminalPageProps) {
  const copy = copyFor(kind, candidateName);
  return (
    <div className="referee-shell flex min-h-svh flex-col items-center justify-center px-6 text-center">
      <ThreadMark className="h-[22px] w-auto" />
      {/*
        The live region WRAPS the heading rather than being put on it. `role="status"` on an <h1>
        REPLACES the implicit heading role instead of adding to it (axe: aria-allowed-role) — the
        text still renders, but it silently vanishes from heading navigation, hurting exactly the
        screen-reader users the announcement is for. Wrapping keeps both: the h1 stays a heading,
        and the container announces.

        Scoped to the title + sentence, not the whole page: a live region re-announces its entire
        contents on change, so the less it holds the less it shouts. These pages are ARRIVED at by
        a redirect rather than chosen, which is why they need announcing at all.
      */}
      <div role="status">
        <h1 className="mt-5 font-display text-[22px] font-semibold tracking-[-0.02em] text-ink text-balance">
          {copy.title}
        </h1>
        <p className="mt-2 max-w-[32ch] font-body text-[13.5px] leading-relaxed text-ink-70 text-pretty">
          {copy.body}
        </p>
      </div>
      {/*
        Actions come BEFORE the quiet closing line, so the reading order on the opt-out page is
        statement → action → the way out ("nothing has happened yet — just close this page"). No
        existing caller passes children, so this ordering costs the §5 pages nothing.

        Width-capped and centred because this page has no column of its own: unlike AppShell it sets
        no max-width, so a `size="block"` button would otherwise stretch the full desktop viewport.
      */}
      {children && (
        <div className="mt-7 flex w-full max-w-[320px] flex-col items-center gap-3">{children}</div>
      )}
      {copy.hint && <p className="mt-6 font-display text-[11px] font-medium text-ink-quiet">{copy.hint}</p>}
    </div>
  );
}
